import { LocalStorageVars } from 'constants/global'
import { GuidedTours, GUIDED_TOURS, WtnPages } from 'constants/guided-tours'
import { SavedWtnInfo } from 'types/global'

export const getStorageKeyForGuidedTours = (tennisId: string) =>
  `${LocalStorageVars.COMPLETED_TOURS}.${tennisId}`

export const getKeysForStepsToComplete = (tennisId: string, page: keyof typeof GUIDED_TOURS) => {
  const allTourSteps = GUIDED_TOURS[page]
  const storedData = localStorage.getItem(getStorageKeyForGuidedTours(tennisId))

  if (!storedData) return allTourSteps

  const completedTours: Partial<GuidedTours> = JSON.parse(storedData)
  const completedSteps = completedTours[page]

  if (!completedSteps) return allTourSteps

  return allTourSteps.filter(step => !completedSteps.includes(step))
}

export const storeKeysForCompletedSteps = <T extends keyof typeof GUIDED_TOURS>(
  tennisId: string,
  page: T,
  steps: Array<(typeof GUIDED_TOURS)[T][number]>
) => {
  const storedData = localStorage.getItem(getStorageKeyForGuidedTours(tennisId))

  if (!storedData) {
    const completedTours = {}
    completedTours[page as WtnPages] = steps
    localStorage.setItem(getStorageKeyForGuidedTours(tennisId), JSON.stringify(completedTours))
  } else {
    const prevCompletedTours = JSON.parse(storedData)
    const completedSteps = [...prevCompletedTours[page], ...steps]
    prevCompletedTours[page] = completedSteps
    localStorage.setItem(getStorageKeyForGuidedTours(tennisId), JSON.stringify(prevCompletedTours))
  }
}

const getStorageKeyForSavedWtnInfo = (tennisId: string) =>
  `${LocalStorageVars.SAVED_USER_INFO}.${tennisId}`

export const saveWtnInfoForUser = (info: SavedWtnInfo, tennisId: string) => {
  localStorage.setItem(getStorageKeyForSavedWtnInfo(tennisId), JSON.stringify(info))
}

export const getSavedWtnInfoForUser = (tennisId: string): SavedWtnInfo => {
  const defaultInfo: SavedWtnInfo = { dontShowMe: [] }
  let userInfo: string | null | SavedWtnInfo = localStorage.getItem(
    getStorageKeyForSavedWtnInfo(tennisId)
  )

  if (userInfo) {
    try {
      userInfo = JSON.parse(userInfo) as SavedWtnInfo
    } catch (e) {
      console.error(e)
      userInfo = defaultInfo
      saveWtnInfoForUser(userInfo, tennisId)
    }
  } else {
    userInfo = defaultInfo
    saveWtnInfoForUser(userInfo, tennisId)
  }
  return userInfo
}

export const doesUserNeedToSee = (item: string, tennisId: string) => {
  if (!tennisId) return true
  const savedUserInfo = getSavedWtnInfoForUser(tennisId)

  return !savedUserInfo.dontShowMe?.includes(item)
}

export const dontShowMeAgain = (item: string, tennisId: string) => {
  if (!tennisId) return
  const savedUserInfo = getSavedWtnInfoForUser(tennisId)

  saveWtnInfoForUser(
    { ...savedUserInfo, dontShowMe: [...savedUserInfo.dontShowMe, item] },
    tennisId
  )
}

export const getStorageKeyForShouldHideProfileStrength = (tennisId: string) =>
  `${LocalStorageVars.HIDE_PROFILE_STRENGTH}.${tennisId}`

export const getShouldHideProfileStrength = (tennisId: string) => {
  return localStorage.getItem(getStorageKeyForShouldHideProfileStrength(tennisId))
}

export const storeShouldHideProfileStrength = (tennisId: string) => {
  localStorage.setItem(getStorageKeyForShouldHideProfileStrength(tennisId), JSON.stringify(true))
}